// TRACE landing — non-translatable data (numbers, prices, colours, surface mock).
// Keep separate from i18n copy. Prices in EUR.
// Relative so CTAs (/login, /register) stay same-origin in dev, preview and prod.
export const APP_URL = "";

export const ACCENTS = ["#ff6a3d", "#e6ff40", "#8b5cf6"] as const; // orange / fluo yellow / violet
// Production: pick ONE, or keep random-per-visit (see LandingPage).

export const STEP_ICONS = ["upload", "scan", "grid", "zap"] as const;
export const FEATURE_ICONS = ["file", "gauge", "changeover", "shield", "users", "mail", "pin"] as const;

// tally counters (align by index with content.tally.boxes)
export const TALLY = [
  { to: 39, unit: "", fill: 78 },
  { to: 22, unit: "", fill: 55 },
  { to: 6, unit: "", fill: 40 },
  { to: 128, unit: "h", fill: 88 },
];

// pricing (align by index with content.pricing.tiers)
export const TIER_PRICES: { monthly: number | null; annual: number | null; hot?: boolean; ctaHref?: string }[] = [
  { monthly: 0, annual: 0 },
  { monthly: 49, annual: 468 },
  { monthly: 149, annual: 1428, hot: true },
  { monthly: 399, annual: 3828 },
  { monthly: null, annual: null, ctaHref: "mailto:info@formray.io" },
];

// cue index for the signal spine (id must match each <section id>)
export const CUES = [
  { sig: "00", id: "cue-input" }, { sig: "01", id: "cue-read" }, { sig: "02", id: "cue-resolve" },
  { sig: "03", id: "cue-arrange" }, { sig: "04", id: "cue-guard" }, { sig: "05", id: "cue-tally" },
  { sig: "06", id: "cue-ask" }, { sig: "07", id: "cue-caps" }, { sig: "08", id: "cue-proof" },
  { sig: "09", id: "cue-plans" }, { sig: "10", id: "cue-faq" }, { sig: "11", id: "cue-output" },
];

// ---- Festival surface (SIG.03) mock data ----
export const STAGE_C: Record<string, string> = { main: "#22d3ee", stone: "#a78bfa", magma: "#f0a35e", sunrise: "#34d399" };
const mins = (h: number, m: number) => h * 60 + m + (h < 12 ? 24 * 60 : 0);
export const FS_T0 = 19 * 60, FS_T1 = 28 * 60;          // timeline: 19:00 → 04:00
export const FS_CREW_T0 = 18 * 60, FS_CREW_T1 = 28 * 60; // crew: shifts start at load-in (18:00)
export const FS_LOGI_T0 = 16 * 60, FS_LOGI_T1 = 28 * 60; // logistics: transport runs from ~16:00
export const FS_HOURS = [19, 20, 21, 22, 23, 24, 25, 26, 27, 28];
export const FS_CREW_TICKS = [18, 20, 22, 24, 26, 28];
export const FS_LOGI_TICKS = [16, 18, 20, 22, 24, 26, 28];

export type Block = [string, number, number, "ok" | "warn" | "tight"];
export const FS_STAGES: { k: string; n: string; sets: number; blocks: Block[] }[] = [
  { k: "sunrise", n: "Sunrise Stage", sets: 3, blocks: [["OVERMONO", mins(19, 0), mins(21, 0), "ok"], ["PEGGY GOU", mins(21, 0), mins(23, 0), "warn"], ["DIXON b2b", mins(23, 0), mins(25, 30), "ok"]] },
  { k: "main", n: "Main Stage", sets: 3, blocks: [["BONOBO", mins(20, 0), mins(22, 0), "ok"], ["TALE OF US", mins(22, 0), mins(24, 0), "warn"], ["FOUR TET", mins(24, 0), mins(26, 0), "ok"]] },
  { k: "magma", n: "Magma Stage", sets: 2, blocks: [["BEN KLOCK", mins(20, 30), mins(23, 0), "ok"], ["BORIS BREJCHA", mins(23, 0), mins(25, 30), "tight"]] },
  { k: "stone", n: "Stone Stage", sets: 3, blocks: [["HAAi", mins(21, 0), mins(23, 0), "ok"], ["SOLOMUN", mins(23, 0), mins(26, 0), "warn"], ["AMELIE LENS", mins(26, 0), mins(28, 0), "ok"]] },
];
export const FS_STAGE_CHIPS = ["Sunrise", "Main", "Magma", "Echoes", "Stone"];
export const FS_GEAR: [string, string][] = [["CDJ 1", "CDJ-3000"], ["CDJ 2", "CDJ-3000"], ["CDJ 3", "CDJ-3000"], ["CDJ 4", "CDJ-3000"], ["TT 1", "SL-1210"], ["Mixer", "DJM-V10"], ["TT 2", "SL-1210"]];
export const FS_CREW: { shifts: [number, number][]; n: number }[] = [
  { shifts: [[mins(18, 0), mins(28, 0)]], n: 1 },
  { shifts: [[mins(19, 0), mins(24, 0)], [mins(24, 0), mins(28, 0)]], n: 2 },
  { shifts: [[mins(19, 0), mins(23, 30)], [mins(23, 0), mins(28, 0)]], n: 4 },
  { shifts: [[mins(20, 0), mins(27, 0)]], n: 1 },
  { shifts: [[mins(19, 0), mins(22, 0)], [mins(23, 30), mins(27, 0)]], n: 2 },
];
export const FS_LOGI: { unit: string; trips: [number, number][] }[] = [
  { unit: "Shuttle A", trips: [[mins(17, 0), mins(19, 0)], [mins(22, 0), mins(24, 0)]] },
  { unit: "Shuttle B", trips: [[mins(18, 30), mins(20, 30)], [mins(25, 0), mins(27, 0)]] },
  { unit: "Artist transfer", trips: [[mins(19, 30), mins(20, 30)], [mins(23, 0), mins(24, 0)], [mins(26, 0), mins(27, 0)]] },
  { unit: "Crew van", trips: [[mins(17, 0), mins(18, 0)], [mins(27, 0), mins(28, 0)]] },
];
export const posIn = (s: number, e: number, t0: number, t1: number) => ({ left: ((s - t0) / (t1 - t0)) * 100, width: ((e - s) / (t1 - t0)) * 100 });
export const fsPos = (s: number, e: number) => posIn(s, e, FS_T0, FS_T1);
export const hhmm = (t: number) => { const h = Math.floor(t / 60) % 24, m = t % 60; return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"); };
